import { Dimensions, Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';

const { width, height } = Dimensions.get('window');

// Synchronous iPad detection for immediate use
export const isIPadSync = () => {
  
  // Method 1: Platform.isPad (immediate check)
  if (Platform.OS === 'ios' && Platform.isPad) {
    return true;
  }
  
  // Method 2: Enhanced dimension-based detection (including compatibility mode)
  if (Platform.OS === 'ios') {
    const aspectRatio = height / width;
    const minDimension = Math.min(width, height);
    const maxDimension = Math.max(width, height);
    
    // More comprehensive iPad detection criteria
    const isIPadByDimensions = aspectRatio <= 1.6 && minDimension >= 768;
    const isIPadByLargeScreen = maxDimension >= 1024;
    
    // Comprehensive list of iPad screen sizes (including newer models and compatibility mode)
    const isIPadByScreenSize = 
      // iPad Mini
      (width === 768 && height === 1024) || // iPad Mini (1st-5th gen)
      (width === 810 && height === 1080) || // iPad Mini 6th gen
      // iPad Air
      (width === 768 && height === 1024) || // iPad Air (1st-2nd gen)
      (width === 810 && height === 1080) || // iPad Air (3rd-4th gen)
      (width === 820 && height === 1180) || // iPad Air 5th gen
      // iPad Pro
      (width === 834 && height === 1112) || // iPad Pro 10.5"
      (width === 834 && height === 1194) || // iPad Pro 11" (1st-3rd gen)
      (width === 834 && height === 1194) || // iPad Pro 11" (4th gen)
      (width === 1024 && height === 1366) || // iPad Pro 12.9" (1st-5th gen)
      // iPad Air 11" M2 (your device)
      (width === 820 && height === 1180) || // iPad Air 11" M2
      // Additional checks for common iPad dimensions (including compatibility mode)
      (minDimension >= 768 && maxDimension >= 1024) ||
      (minDimension >= 820 && maxDimension >= 1180) ||
      (minDimension >= 834 && maxDimension >= 1194) ||
      (minDimension >= 810 && maxDimension >= 1080);
    
    // Method 3: User agent check (for web)
    const isIPadByUserAgent = typeof navigator !== 'undefined' && 
      navigator.userAgent && 
      (navigator.userAgent.includes('iPad') || navigator.userAgent.includes('Macintosh'));
    
    // Method 4: Modern iPad detection (specifically for iPad Air 11" M2 and similar)
    const isModernIPad = detectModernIPad();
    
    // Method 5: Compatibility mode detection (when app thinks it's iPhone but has iPad dimensions)
    const isCompatibilityMode = aspectRatio <= 1.6 && minDimension >= 768 && maxDimension >= 1024;
    
    // Method 6: iPhone compatibility mode detection (when iPad shows iPhone dimensions)
    const isIPhoneCompatibilityMode = 
      // Check if we have iPhone dimensions but are on iOS (likely iPad in compatibility mode)
      (width === 375 && height === 667) || // iPhone 6/7/8
      (width === 375 && height === 812) || // iPhone X/XS/11 Pro
      (width === 414 && height === 896) || // iPhone XR/11
      (width === 390 && height === 844) || // iPhone 12/13/14
      (width === 428 && height === 926);   // iPhone 14 Plus/15 Plus
    
    return isIPadByDimensions || isIPadByLargeScreen || isIPadByScreenSize || isIPadByUserAgent || isModernIPad || isCompatibilityMode || isIPhoneCompatibilityMode;
  }
  
  return false;
};

// Enhanced iPad detection using react-native-device-info (async version)
export const isIPad = async () => {
  
  try {
    // Method 1: DeviceInfo.isTablet() - Most reliable method
    const isTablet = DeviceInfo.isTablet();
    
    if (isTablet) {
      return true;
    }
    
    // Method 2: Check for iPad in compatibility mode
    const isIPadInCompatibilityMode = await detectIPadCompatibilityMode();
    if (isIPadInCompatibilityMode) {
      return true;
    }
    
    // Fallback to synchronous detection
    return isIPadSync();
  } catch (error) {
    // Fallback to synchronous detection if DeviceInfo fails
    return isIPadSync();
  }
};

// Get device type for responsive design (synchronous)
export const getDeviceType = () => {
  if (Platform.OS === 'ios') {
    // Use Platform.isPad for primary detection
    if (Platform.isPad) {
      return 'ipad';
    }
    
    // Use the same enhanced detection logic as isIPadSync()
    const aspectRatio = height / width;
    const minDimension = Math.min(width, height);
    const maxDimension = Math.max(width, height);
    
    // More comprehensive iPad detection criteria
    const isIPadByDimensions = aspectRatio <= 1.6 && minDimension >= 768;
    const isIPadByLargeScreen = maxDimension >= 1024;
    
    // Comprehensive list of iPad screen sizes (including newer models and compatibility mode)
    const isIPadByScreenSize = 
      // iPad Mini
      (width === 768 && height === 1024) || // iPad Mini (1st-5th gen)
      (width === 810 && height === 1080) || // iPad Mini 6th gen
      // iPad Air
      (width === 768 && height === 1024) || // iPad Air (1st-2nd gen)
      (width === 810 && height === 1080) || // iPad Air (3rd-4th gen)
      (width === 820 && height === 1180) || // iPad Air 5th gen
      // iPad Pro
      (width === 834 && height === 1112) || // iPad Pro 10.5"
      (width === 834 && height === 1194) || // iPad Pro 11" (1st-3rd gen)
      (width === 834 && height === 1194) || // iPad Pro 11" (4th gen)
      (width === 1024 && height === 1366) || // iPad Pro 12.9" (1st-5th gen)
      // iPad Air 11" M2 (your device)
      (width === 820 && height === 1180) || // iPad Air 11" M2
      // Additional checks for common iPad dimensions (including compatibility mode)
      (minDimension >= 768 && maxDimension >= 1024) ||
      (minDimension >= 820 && maxDimension >= 1180) ||
      (minDimension >= 834 && maxDimension >= 1194) ||
      (minDimension >= 810 && maxDimension >= 1080);
    
    // User agent check (for web)
    const isIPadByUserAgent = typeof navigator !== 'undefined' && 
      navigator.userAgent && 
      (navigator.userAgent.includes('iPad') || navigator.userAgent.includes('Macintosh'));
    
    // Modern iPad detection
    const isModernIPad = detectModernIPad();
    
    // Compatibility mode detection (when app thinks it's iPhone but has iPad dimensions)
    const isCompatibilityMode = aspectRatio <= 1.6 && minDimension >= 768 && maxDimension >= 1024;
    
    // iPhone compatibility mode detection (when iPad shows iPhone dimensions)
    const isIPhoneCompatibilityMode = 
      // Check if we have iPhone dimensions but are on iOS (likely iPad in compatibility mode)
      (width === 375 && height === 667) || // iPhone 6/7/8
      (width === 375 && height === 812) || // iPhone X/XS/11 Pro
      (width === 414 && height === 896) || // iPhone XR/11
      (width === 390 && height === 844) || // iPhone 12/13/14
      (width === 428 && height === 926);   // iPhone 14 Plus/15 Plus
    
    const isIPadDevice = isIPadByDimensions || isIPadByLargeScreen || isIPadByScreenSize || isIPadByUserAgent || isModernIPad || isCompatibilityMode || isIPhoneCompatibilityMode;
    
    if (isIPadDevice) {
      return 'ipad';
    }
    
    return 'iphone';
  }
  
  return 'android';
};

// Enhanced iPad detection for modern devices
export const detectModernIPad = () => {
  const aspectRatio = height / width;
  const minDimension = Math.min(width, height);
  const maxDimension = Math.max(width, height);
  
  // Specific detection for iPad Air 11" M2 and similar modern iPads
  const isModernIPad = 
    // iPad Air 11" M2 specific dimensions (exact match)
    (width === 820 && height === 1180) ||
    // iPad Pro 11" dimensions
    (width === 834 && height === 1194) ||
    // iPad Air 10.9" dimensions
    (width === 810 && height === 1080) ||
    // General modern iPad criteria
    (minDimension >= 800 && maxDimension >= 1100) ||
    (aspectRatio <= 1.5 && minDimension >= 800) ||
    // Additional criteria for iPad Air 11" M2 in compatibility mode
    (minDimension >= 820 && maxDimension >= 1180) ||
    (aspectRatio >= 1.4 && aspectRatio <= 1.5 && minDimension >= 820);
  
  return isModernIPad;
};

// Detect iPad running in compatibility mode
export const detectIPadCompatibilityMode = async () => {
  try {
    // In compatibility mode, the app thinks it's running on iPhone but has iPad dimensions
    const deviceType = await DeviceInfo.getDeviceType();
    const isTablet = await DeviceInfo.isTablet();
    
    // If DeviceInfo says it's not a tablet but we have iPad-like dimensions, it's likely compatibility mode
    if (deviceType === 'Handset' && !isTablet && Platform.OS === 'ios') {
      const aspectRatio = height / width;
      const minDimension = Math.min(width, height);
      const maxDimension = Math.max(width, height);
      
      // Check for iPad dimensions in compatibility mode
      const isIPadDimensions = 
        // Common iPad dimensions in compatibility mode
        (minDimension >= 768 && maxDimension >= 1024) ||
        (minDimension >= 820 && maxDimension >= 1180) ||
        (minDimension >= 834 && maxDimension >= 1194) ||
        (minDimension >= 810 && maxDimension >= 1080) ||
        // Aspect ratio check for iPad-like proportions
        (aspectRatio <= 1.6 && minDimension >= 768) ||
        // Specific iPad Air 11" M2 dimensions
        (width === 820 && height === 1180);
      
      return isIPadDimensions;
    }
    
    return false;
  } catch (error) {
    return false;
  }
};

// Responsive dimensions
export const getResponsiveDimensions = () => {
  const deviceType = getDeviceType();
  const isIPadDevice = isIPadSync();
  
  if (deviceType === 'ipad' || isIPadDevice) {
    // Calculate aspect ratio to handle compatibility mode issues
    const aspectRatio = height / width;
    const minDimension = Math.min(width, height);
    const maxDimension = Math.max(width, height);
    
    // Adjust dimensions based on aspect ratio and screen size
    let maxContentWidth = 768; // Default iPad max width
    let footerHeight = 65;
    let paddingHorizontal = 40;
    
    // Handle different iPad sizes and orientations
    if (aspectRatio <= 1.3) {
      // Landscape orientation - wider content
      maxContentWidth = Math.min(width * 0.8, 1024);
      paddingHorizontal = 60;
    } else if (aspectRatio >= 1.4) {
      // Portrait orientation - narrower content
      maxContentWidth = Math.min(width * 0.9, 768);
      paddingHorizontal = 40;
    }
    
    // Adjust for very large screens (iPad Pro 12.9")
    if (maxDimension >= 1366) {
      maxContentWidth = Math.min(width * 0.7, 1200);
      paddingHorizontal = 80;
      footerHeight = 85; // Increased from 80 to 85
    }
    
    // Adjust for smaller iPads (iPad Mini)
    if (minDimension <= 768) {
      maxContentWidth = Math.min(width * 0.95, 600);
      paddingHorizontal = 30;
      footerHeight = 70; // Increased from 60 to 70
    }
    
    return {
      // iPad-specific dimensions with aspect ratio adjustments
      maxContentWidth,
      headerHeight: 80,
      footerHeight,
      paddingHorizontal,
      paddingVertical: 20,
      borderRadius: 12,
      fontSize: {
        small: 14,
        medium: 16,
        large: 18,
        xlarge: 24,
        xxlarge: 32,
      },
      spacing: {
        xs: 8,
        sm: 12,
        md: 16,
        lg: 24,
        xl: 32,
        xxl: 48,
      },
      iconSize: {
        small: 24,
        medium: 32,
        large: 48,
        xlarge: 64,
      },
    };
  }
  
  // iPhone/Android dimensions
  return {
    maxContentWidth: width,
    headerHeight: 60,
    footerHeight: 80,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    fontSize: {
      small: 12,
      medium: 14,
      large: 16,
      xlarge: 20,
      xxlarge: 24,
    },
    spacing: {
      xs: 4,
      sm: 8,
      md: 12,
      lg: 16,
      xl: 24,
      xxl: 32,
    },
    iconSize: {
      small: 20,
      medium: 24,
      large: 32,
      xlarge: 48,
    },
  };
};

// Responsive scaling for iPad
export const scaleForIPad = (size: number) => {
  const deviceType = getDeviceType();
  if (deviceType === 'ipad') {
    return size * 0.9; // Scale down slightly for iPad
  }
  return size;
};

// Get safe area adjustments for iPad
export const getIPadSafeAreaAdjustments = () => {
  const deviceType = getDeviceType();
  if (deviceType !== 'ipad') return { paddingHorizontal: 0, maxWidth: '100%' };
  
  return {
    paddingHorizontal: scaleForIPad(40),
    maxWidth: scaleForIPad(600), // Center content on iPad
  };
};

// Responsive layout helpers
export const getResponsiveLayout = () => {
  const deviceType = getDeviceType();
  
  if (deviceType === 'ipad') {
    return {
      // Center content on iPad with max width
      containerStyle: {
        flex: 1,
        maxWidth: 768,
        alignSelf: 'center',
        width: '100%',
      },
      // Two-column layout for iPad
      twoColumnLayout: {
        flexDirection: 'row' as const,
        gap: 24,
      },
      // Responsive grid
      gridLayout: {
        flexDirection: 'row' as const,
        flexWrap: 'wrap' as const,
        gap: 16,
      },
    };
  }
  
  return {
    containerStyle: {
      flex: 1,
    },
    twoColumnLayout: {
      flexDirection: 'column' as const,
      gap: 16,
    },
    gridLayout: {
      flexDirection: 'column' as const,
      gap: 12,
    },
  };
};

